import { appSession } from './safeStorage';
/**
 * Chunk-load failure — the ONE place the whole app decides what happens when a
 * code/style chunk fails to load. (BLOCK I)
 *
 * There used to be two systems answering this question: BuildUpdateBar's own
 * error/unhandledrejection listeners, and the route-level ErrorBoundary. Only
 * one recovered, so a rejected dynamic import inside an event handler, or a
 * failure that surfaced through the boundary, left the user on a dead screen —
 * and the Error was logged even when recovery worked, so a self-healed event
 * and a stuck user looked identical in Sentry.
 *
 * Now there is exactly one decision funnel — `handleChunkFailure` — fed by three
 * entry points that all call it:
 *   1. a capture-phase `error` listener for failed <script>/<link> under
 *      /static/js|css,
 *   2. an `unhandledrejection` listener for failed dynamic imports,
 *   3. `handleBoundaryChunkError`, called by the ErrorBoundary.
 *
 * Recovery is ONE cache-busted reload per incident, guarded by a single
 * appSession marker, and the telemetry finally tells the truth: an Error in
 * this class means a real person was left looking at a broken page.
 */
import * as Sentry from '@sentry/react';
import { isClientFacingPath } from './publicRoutes';
import { currentRedactedPath, redactString } from './redactTokens';
import { isDeveloperEnvironment } from './appEnvironment';

const MAIN_HASH_RE = /\/static\/js\/main\.([A-Za-z0-9]+)\.js/;
// Cache-busting query param appended to the current URL on reload.
const STAMP_PARAM = '_v';
// The single fixed key holding the one-retry marker (a timestamp).
const STORAGE_KEY = 'staffed_chunk_recovery';
// A marker younger than this means the reload we just did did NOT fix it — do
// not reload again; show the banner. Older than this is a fresh incident.
const RECOVERY_TTL_MS = 60000;

export function isStaleBuildError(err) {
  const msg = String((err && (err.message || err.reason?.message || err.reason)) || err || '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    (/Unexpected token/i.test(msg) && /</.test(msg))
  );
}

/** The raw evidence carried by webpack's / the browser's own error message. */
export function chunkFailureDetail(err) {
  const message = String((err && (err.message || err.reason?.message || err.reason)) || err || '');
  const reason = (message.match(/\((timeout|error|missing)\s*:/i) || [])[1] || 'error';
  const chunkUrl = (message.match(/https?:\/\/[^\s)]+\.(?:js|css)/) || [])[0] || null;
  return { message, reason: String(reason).toLowerCase(), chunkUrl };
}

export function bootBuildHash() {
  try {
    const el = document.querySelector('script[src*="/static/js/main."]');
    const m = el && el.src.match(MAIN_HASH_RE);
    return m ? m[1] : 'unknown';
  } catch {
    return 'unknown';
  }
}

/** The hash the SERVER is currently shipping, read past every cache. */
export async function fetchCurrentBuildHash() {
  if (window.__staffedLatestBuildHash) return window.__staffedLatestBuildHash;
  try {
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return 'unreadable';
    const m = (await res.text()).match(MAIN_HASH_RE);
    return m ? m[1] : 'unreadable';
  } catch {
    return 'unreadable';
  }
}

/** What the server says about the file that failed: a status, or no response. */
export async function probeChunk(url) {
  if (!url) return { status: 'not-recorded', elapsedMs: null };
  const started = Date.now();
  try {
    const res = await fetch(url, { cache: 'reload', credentials: 'omit' });
    return { status: res.status, elapsedMs: Date.now() - started };
  } catch {
    return { status: 'no-response', elapsedMs: Date.now() - started };
  }
}

/** Age of the one-retry marker in ms. Infinity when there is no marker. */
function markerAgeMs() {
  let marker = 0;
  try { marker = Number(appSession.getItem(STORAGE_KEY) || 0); } catch { /* blocked */ }
  if (!marker || Number.isNaN(marker)) return Infinity;
  // Math.abs keeps a future-skewed clock on the safe side (blocks a loop).
  return Math.abs(Date.now() - marker);
}

/**
 * Reload ONCE onto a URL no HTML cache can answer. Writes the marker first, and
 * preserves the full path, existing query string and hash so the person lands
 * back where they were (/signup reloads as /signup).
 */
export function hardRefresh() {
  // BLOCK S12 Item 4b — mark that chunk recovery is acting so the render
  // watchdog defers to it (chunk recovery goes first). Behaviour is otherwise
  // unchanged; a reload follows immediately below.
  try { window.__staffedChunkRecovering = true; } catch { /* noop */ }
  try { appSession.setItem(STORAGE_KEY, String(Date.now())); } catch { /* noop */ }
  // Best effort, fire-and-forget: purge anything a previous service-worker
  // version left behind. Never awaited — the reload must not wait on it.
  try {
    if (window.caches?.keys) {
      window.caches.keys().then((keys) => keys.forEach((k) => window.caches.delete(k))).catch(() => {});
    }
  } catch { /* noop */ }
  try {
    const u = new URL(window.location.href);
    u.searchParams.set(STAMP_PARAM, String(Date.now()));
    window.location.replace(u.toString()); // keeps path + query + hash, adds _v
  } catch {
    window.location.reload();
  }
}

/** App mounted successfully → drop the marker so a later, unrelated failure
 * gets its own single retry. */
export function clearRecoveryMarker() {
  try { appSession.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

/**
 * Take the cache-bust param back out of the address bar once the page is up.
 * Client portal links get forwarded to spouses, planners and parents — the
 * link they copy must be the clean one.
 */
export function cleanRecoveryParams() {
  try {
    const u = new URL(window.location.href);
    if (!u.searchParams.has(STAMP_PARAM)) return false;
    u.searchParams.delete(STAMP_PARAM);
    const search = u.searchParams.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${u.pathname}${search ? `?${search}` : ''}${u.hash || ''}`,
    );
    return true;
  } catch {
    return false;
  }
}

// ── Telemetry ───────────────────────────────────────────────────────────────

function reportRecovering(error, synthetic) {
  const info = chunkFailureDetail(error);
  const clientFacing = isClientFacingPath(window.location.pathname);
  // The reload cancels in-flight requests, so this must be sent synchronously —
  // no async probe on the recovering path.
  try {
    Sentry.captureMessage('chunk-load: recovering', {
      level: 'info',
      tags: {
        stale_build: 'true',
        recovery_attempted: 'true',
        chunk_failure_reason: info.reason,
        chunk_url: info.chunkUrl || 'not-recorded',
        client_facing: String(clientFacing),
        client_sample_rate: clientFacing ? '1.0' : '0.1',
        synthetic: String(!!synthetic),
      },
      extra: {
        path: currentRedactedPath(),
        chunkUrl: info.chunkUrl || 'not-recorded',
        chunkFailureReason: info.reason,
        detail: redactString(info.message).slice(0, 300),
        bootBuildHash: bootBuildHash(),
      },
    });
  } catch { /* reporting must never break recovery */ }
}

function reportExhausted(error, synthetic) {
  const info = chunkFailureDetail(error);
  const clientFacing = isClientFacingPath(window.location.pathname);
  // No navigation on this path, so there is time to probe the failing URL and
  // read the shipping hash — that is what settles 404-versus-timeout.
  (async () => {
    let httpStatus = 'not-probed';
    let currentBuildHash = window.__staffedLatestBuildHash || 'unknown';
    try {
      const [probe, hash] = await Promise.all([probeChunk(info.chunkUrl), fetchCurrentBuildHash()]);
      httpStatus = probe.status;
      currentBuildHash = hash;
    } catch { /* evidence is best-effort */ }
    try {
      Sentry.captureMessage('chunk-load: recovery exhausted', {
        level: 'error',
        tags: {
          stale_build: 'true',
          recovery_attempted: 'false',
          chunk_failure_reason: info.reason,
          chunk_url: info.chunkUrl || 'not-recorded',
          client_facing: String(clientFacing),
          client_sample_rate: clientFacing ? '1.0' : '0.1',
          synthetic: String(!!synthetic),
        },
        extra: {
          path: currentRedactedPath(),
          chunkUrl: info.chunkUrl || 'not-recorded',
          chunkFailureReason: info.reason,
          httpStatus,
          currentBuildHash,
          detail: redactString(info.message).slice(0, 300),
          bootBuildHash: bootBuildHash(),
        },
      });
    } catch { /* noop */ }
  })();
}

// ── The one decision funnel ──────────────────────────────────────────────────

let reportedThisBoot = false;

/**
 * THE single place that decides what happens on a recognised chunk failure.
 * One automatic reload per incident, guarded by the appSession marker.
 */
export function handleChunkFailure(error, { synthetic = false } = {}) {
  // A synthetic (developer-triggered) event must never reach the shared error
  // project — it inflates the affected-client count on a real issue.
  if (synthetic && !isDeveloperEnvironment()) return;
  try { window.__staffedChunkFailedThisBoot = true; } catch { /* noop */ }

  const recentMarker = markerAgeMs() < RECOVERY_TTL_MS;
  if (recentMarker) {
    // The one reload was already used and it did not fix it → show the banner,
    // do NOT reload again.
    if (!reportedThisBoot) { reportedThisBoot = true; reportExhausted(error, synthetic); }
    try { window.dispatchEvent(new CustomEvent('staffed:chunkstuck')); } catch { /* noop */ }
    return;
  }
  // No marker, or a marker older than the TTL (a fresh incident) → reload once.
  if (!reportedThisBoot) { reportedThisBoot = true; reportRecovering(error, synthetic); }
  hardRefresh();
}

/** Called by the route-level ErrorBoundary. Returns true if the error was a
 * chunk failure (so the boundary skips its dead-screen and lets this decide). */
export function handleBoundaryChunkError(error) {
  if (!isStaleBuildError(error)) return false;
  handleChunkFailure(error);
  return true;
}

let installed = false;

/** Install the two global listeners once, at app startup. */
export function installChunkFailureHandler() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 1. Failed <script>/<link> load. Resource-load errors do not bubble, so this
  //    must be a CAPTURE listener and inspect e.target.
  window.addEventListener('error', (e) => {
    const t = e && e.target;
    if (t && (t.tagName === 'SCRIPT' || t.tagName === 'LINK')) {
      const src = t.src || t.href || '';
      if (/\/static\/(?:js|css)\//.test(src)) {
        handleChunkFailure(new Error(`Loading chunk failed: ${src}`));
      }
      return;
    }
    const asError = e && e.error ? e.error : (e && e.message) || '';
    if (isStaleBuildError(asError)) handleChunkFailure(asError);
  }, true);

  // 2. Rejected dynamic import.
  window.addEventListener('unhandledrejection', (e) => {
    if (isStaleBuildError(e && e.reason)) handleChunkFailure(e.reason);
  });

  // Developer-only manual trigger (synthetic; a no-op off a developer machine).
  window.addEventListener('staffed:stalechunk', () => {
    handleChunkFailure('manual stale chunk failure', { synthetic: true });
  });
}
